import { HttpBackend, HttpClient } from '@angular/common/http';
import { Injectable, isDevMode, NgZone, OnDestroy } from '@angular/core';
import { NavigationEnd, Router } from '@angular/router';
import {
  firstValueFrom,
  fromEvent,
  interval,
  Subscription,
} from 'rxjs';
import { AnalyticsService } from './analytics.service';
import { ReloadReadinessService } from './reload-readiness.service';

interface VersionManifest {
  version: string;
  buildTime?: string;
}

const POLL_INTERVAL_MS = 10 * 60 * 1000; // 10 minutes
const MIN_POLL_GAP_MS = 60 * 1000; // rate-limit checks triggered by nav/focus
const VERSION_URL = '/assets/version.json';

/**
 * Detects new deploys by polling /assets/version.json and applies them
 * with a silent reload on the next safe "natural exit" navigation. No
 * user prompt — matches the convention of modern SaaS (Gmail, Notion,
 * Linear, etc.) where updates happen transparently.
 *
 * Silent reload requires:
 * - a pending new version, AND
 * - a natural-exit nav (crossing a top-level module or decreasing route
 *   depth), AND
 * - the readiness service reporting safe (no dirty forms, no in-flight
 *   mutations, no open dialogs).
 *
 * Users mid-edit never lose work; they pick up the new code the next
 * time they reach a safe boundary — typically within minutes of any
 * deploy, since crossing a top-level module is extremely common during
 * normal use.
 */
@Injectable({ providedIn: 'root' })
export class AppUpdateService implements OnDestroy {
  private http: HttpClient;
  private bootVersion: string | null = null;
  private latestVersion: string | null = null;
  private pendingSince: number | null = null;
  private lastPollAt = 0;
  private lastUrl: string | null = null;
  private subscriptions: Subscription[] = [];
  private started = false;

  constructor(
    private router: Router,
    private readiness: ReloadReadinessService,
    private analytics: AnalyticsService,
    private zone: NgZone,
    httpBackend: HttpBackend,
  ) {
    // Bypass HTTP interceptors (auth token, etc.) for a pure static fetch.
    this.http = new HttpClient(httpBackend);
  }

  /**
   * Starts polling + event listeners. Call from AppComponent.ngOnInit.
   * No-op in development so local `ng serve` doesn't reload itself.
   */
  async start(): Promise<void> {
    if (this.started) return;
    this.started = true;

    // No-op for any local `ng serve` (default or beta config). `ng serve`
    // doesn't run the prebuild script, so /assets/version.json doesn't
    // exist locally — fetching it would just spam a 404. isDevMode() is
    // false only in production builds where enableProdMode() runs.
    if (isDevMode()) return;

    const initial = await this.fetchManifest();
    if (!initial) return;

    console.log(
      `[app-update] running version ${initial.version}` +
        (initial.buildTime ? ` (built ${initial.buildTime})` : ''),
    );
    // Exposed for ad-hoc support inspection in the browser console.
    (window as Window & { __appVersion?: VersionManifest }).__appVersion = initial;

    this.bootVersion = initial.version;
    this.latestVersion = initial.version;

    // Run outside Angular so the periodic timer doesn't churn change detection.
    this.zone.runOutsideAngular(() => {
      this.subscriptions.push(
        interval(POLL_INTERVAL_MS).subscribe(() => this.poll('timer')),
      );
      this.subscriptions.push(
        fromEvent(document, 'visibilitychange').subscribe(() => {
          if (document.visibilityState === 'visible') this.poll('visibility');
        }),
      );
      this.subscriptions.push(
        fromEvent(window, 'online').subscribe(() => this.poll('online')),
      );
    });

    this.subscriptions.push(
      this.router.events.subscribe((event) => {
        if (event instanceof NavigationEnd) {
          // Polling triggers an HTTP fetch; keep it out of the zone so
          // every navigation doesn't tick change detection on resolve.
          this.zone.runOutsideAngular(() => this.poll('navigation'));
          this.maybeApplyPending(event);
        }
      }),
    );
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((s) => s.unsubscribe());
  }

  private async poll(trigger: 'timer' | 'visibility' | 'online' | 'navigation'): Promise<void> {
    const now = Date.now();
    if (trigger !== 'timer' && now - this.lastPollAt < MIN_POLL_GAP_MS) {
      return;
    }
    this.lastPollAt = now;

    const manifest = await this.fetchManifest();
    if (!manifest || !this.bootVersion) return;

    if (manifest.version === this.bootVersion) {
      // Back to the version we booted with — possible during rollback.
      if (this.latestVersion !== this.bootVersion) {
        this.latestVersion = this.bootVersion;
        this.pendingSince = null;
      }
      return;
    }

    if (manifest.version !== this.latestVersion) {
      this.latestVersion = manifest.version;
      this.pendingSince = now;
      console.log(
        `[app-update] new version detected via ${trigger}: ${manifest.version} ` +
          `(boot was ${this.bootVersion}) — will reload on next safe navigation`,
      );
      this.analytics.capture('app_update.detected', {
        trigger,
        boot_version: this.bootVersion,
        new_version: manifest.version,
      });
    }
  }

  private maybeApplyPending(event: NavigationEnd): void {
    // Always advance lastUrl so a long stay on one route doesn't leave
    // the natural-exit check comparing against a stale prior URL once an
    // update finally lands.
    const prevUrl = this.lastUrl;
    this.lastUrl = event.urlAfterRedirects;

    if (!this.hasPendingUpdate()) return;
    if (!this.isNaturalExit(event, prevUrl)) return;

    const state = this.readiness.snapshot();
    if (!state.safe) {
      console.log(`[app-update] reload deferred — ${state.reason}`);
      this.analytics.capture('app_update.deferred', {
        reason: state.reason,
        new_version: this.latestVersion,
      });
      return;
    }

    console.log(`[app-update] applying silent reload to ${this.latestVersion}`);
    this.analytics.capture('app_update.applied_silent', {
      new_version: this.latestVersion,
      pending_duration_ms: this.pendingSince ? Date.now() - this.pendingSince : 0,
    });
    this.reload();
  }

  private hasPendingUpdate(): boolean {
    return !!(this.latestVersion && this.bootVersion && this.latestVersion !== this.bootVersion);
  }

  private isNaturalExit(event: NavigationEnd, prevUrl: string | null): boolean {
    if (!prevUrl) return false;
    const nextUrl = event.urlAfterRedirects;

    const prevRoot = topLevelSegment(prevUrl);
    const nextRoot = topLevelSegment(nextUrl);

    // Crossing a top-level module boundary (e.g. /projects → /dashboard)
    if (prevRoot !== nextRoot) return true;

    // Leaving a deep path for a shallower one (e.g. /rfis/123/edit → /rfis)
    const prevDepth = prevUrl.split('?')[0].split('/').filter(Boolean).length;
    const nextDepth = nextUrl.split('?')[0].split('/').filter(Boolean).length;
    return nextDepth < prevDepth;
  }

  private reload(): void {
    // Escape Angular's zone so the reload isn't caught as a navigation error.
    this.zone.runOutsideAngular(() => window.location.reload());
  }

  private async fetchManifest(): Promise<VersionManifest | null> {
    const url = `${VERSION_URL}?t=${Date.now()}`;
    try {
      return await firstValueFrom(this.http.get<VersionManifest>(url));
    } catch {
      return null;
    }
  }
}

function topLevelSegment(url: string): string {
  const path = url.split('?')[0];
  const segments = path.split('/').filter(Boolean);
  return segments[0] ?? '';
}
